import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/prim-target-settings`;
const lines = (t) => t.split('\n').map(s => s.trim()).filter(Boolean);

export function PrimTargetSettingsDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [info, setInfo] = useState({ unmatched: [], catalog: [] });
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}?token=${token}`).then(({ data }) => {
      setForm({
        camera_target: String(data.camera_target), device_target: String(data.device_target),
        camera_products: data.camera_products.join('\n'), device_products: data.device_products.join('\n'),
        exempt_usernames: data.exempt_usernames.join('\n')
      });
      setInfo({ unmatched: data.unmatched_products, catalog: data.catalog_products });
    }).catch(() => toast.error('Ayarlar yüklenemedi'));
  }, [open, token]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}?token=${token}`, {
        camera_target: parseInt(form.camera_target || '0', 10), device_target: parseInt(form.device_target || '0', 10),
        camera_products: lines(form.camera_products), device_products: lines(form.device_products),
        exempt_usernames: lines(form.exempt_usernames)
      });
      toast.success('Ayarlar kaydedildi');
      onSaved();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prim-target-settings-dialog">
        <DialogHeader><DialogTitle>Kamera / Cihaz Hedef Ayarları</DialogTitle></DialogHeader>
        {!form ? <p className="text-sm text-gray-500">Yükleniyor...</p> : (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Kasım 2026 ve sonrası temsilciler için geçerlidir. İki hedef birlikte tutmazsa prim en fazla 1. baremden hesaplanır.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Aylık Kamera Hedefi (adet)</Label><Input data-testid="prim-camera-target-input" type="number" min="0" value={form.camera_target} onChange={set('camera_target')} /></div>
              <div><Label>Aylık Cihaz Hedefi (adet)</Label><Input data-testid="prim-device-target-input" type="number" min="0" value={form.device_target} onChange={set('device_target')} /></div>
            </div>
            <div><Label>Kamera hedefine dahil ürünler (her satıra bir ürün)</Label><Textarea data-testid="prim-camera-products-input" rows={4} value={form.camera_products} onChange={set('camera_products')} /></div>
            <div><Label>Cihaz hedefine dahil ürünler (her satıra bir ürün)</Label><Textarea data-testid="prim-device-products-input" rows={8} value={form.device_products} onChange={set('device_products')} /></div>
            <div><Label>Kuraldan muaf kullanıcılar (kullanıcı adı veya ad soyad)</Label><Textarea data-testid="prim-exempt-input" rows={2} value={form.exempt_usernames} onChange={set('exempt_usernames')} /></div>
            {info.unmatched.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="prim-unmatched-products">
                <p className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Ürün kataloğunda bulunamayan adlar ({info.unmatched.length})</p>
                <p className="mb-1">Bu adlar katalogdaki ürün adlarıyla eşleşmiyor; satışlar yine satış satırındaki ürün adına göre sayılır. Gerekirse adı katalogdaki gibi düzeltin.</p>
                <ul className="list-disc pl-4">{info.unmatched.map(n => <li key={n}>{n}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button data-testid="prim-settings-save-btn" onClick={save} disabled={busy || !form}>{busy ? 'Kaydediliyor...' : 'Kaydet'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
